import { createIcon } from '../../icons';

export class ItemCardSameReasons {
  constructor(element) {
    this._el = element;
    this._checkbox = element.querySelector('input[type="checkbox"]');
    this._mergedCard = null;
    this._mergedCardTouched = false;
    this._movedConfiguration = null;
    this._savedCardSelections = null;

    let config;
    try {
      config = JSON.parse(element.dataset.itemCardsSameReasonsConfig || '{}');
    } catch {
      config = {};
    }

    this._titleTemplate = config.mergedCardTitleTemplate || '{count} items';
    this._deleteHref = config.deleteHref || '/';
    this._serialSidebarConfig = config.serialSidebar || {};
    this._patientSidebarConfig = { ...(config.patientSidebar || {}), columns: 1 };

    if (this._checkbox) {
      this._checkbox.addEventListener('change', () => {
        if (this._checkbox.checked) {
          this._merge();
        } else {
          this._unmerge();
        }

        // Merge/unmerge - start validation from a clean state.
        document.dispatchEvent(new CustomEvent('item-cards-same-reasons:toggle'));
      });
    }
  }

  _getIndividualCards() {
    return [...document.querySelectorAll('[data-item-card-inactive]')];
  }

  _extractCardData(card) {
    let config;
    try {
      config = JSON.parse(card.dataset.itemCardConfig || '{}');
    } catch {
      config = {};
    }

    const rows = card.querySelectorAll('.item-card__product-row');

    return {
      serialNo: config.serialNo || '',
      serialNoLabel: rows[0]?.querySelector('.item-card__product-label')?.textContent.trim() || '',
      patientName: rows[1]?.querySelector('.item-card__product-value')?.textContent.trim() || '',
      patientNameLabel:
        rows[1]?.querySelector('.item-card__product-label')?.textContent.trim() || '',
    };
  }

  _merge() {
    const cards = [...document.querySelectorAll('[data-item-card]:not(.item-card--merged)')];
    if (cards.length === 0) return;

    // Snapshot every card's field state before the merged card is reset, so an
    // untouched unmerge can restore each card to exactly its previous state.
    this._savedCardSelections = cards.map((card) =>
      this._captureState(card.querySelector('.item-card__configurator-mount')),
    );

    const cardData = cards.map((card) => this._extractCardData(card));
    const serialNos = cardData.map((d) => d.serialNo).filter(Boolean);
    const patientNames = cardData.map((d) => d.patientName).filter((s) => s && s !== '-');
    const { serialNoLabel, patientNameLabel } = cardData[0];
    const title = this._titleTemplate.replace('{count}', String(cards.length));

    const firstCard = cards[0];
    const configuration = firstCard.querySelector('.item-card__configurator-mount');
    if (!configuration) return;

    this._movedConfiguration = {
      element: configuration,
      parent: configuration.parentElement,
    };

    this._mergedCard = this._buildMergedCard({
      title,
      serialNos,
      patientNames,
      serialNoLabel,
      patientNameLabel,
      configuration,
    });

    firstCard.parentElement.insertBefore(this._mergedCard, firstCard);

    // Wire remove button to dispatch CustomEvent — consistent with other dialog/sidebar openers
    this._mergedCard.querySelector('.item-card__remove')?.addEventListener('click', () => {
      document.dispatchEvent(
        new CustomEvent('remove-items-dialog:open', {
          detail: { count: cards.length, href: this._deleteHref },
        }),
      );
    });

    this._mergedCardTouched = false;

    // Remove data-item-card so ServiceFlowWarranty skips hidden individual cards
    cards.forEach((card) => {
      card.setAttribute('hidden', '');
      card.setAttribute('data-item-card-inactive', '');
      card.removeAttribute('data-item-card');
    });

    // Merged card starts fresh: clear only selected values, keeping
    // placeholders, labels and translations untouched.
    this._resetState(this._movedConfiguration.element);

    // Mark merged card as touched on the first configuration interaction.
    this._mergedCard.addEventListener('click', (e) => {
      if (e.target.closest('.item-card__configurator-mount') && !this._mergedCardTouched) {
        this._mergedCardTouched = true;
      }
    });
    this._mergedCard.addEventListener('change', () => {
      this._mergedCardTouched = true;
    });
    this._mergedCard.addEventListener('input', () => {
      this._mergedCardTouched = true;
    });
  }

  _buildMergedCard({
    title,
    serialNos,
    patientNames,
    serialNoLabel,
    patientNameLabel,
    configuration,
  }) {
    const card = document.createElement('div');
    card.className = 'item-card item-card--merged';
    card.setAttribute('data-item-card', '');
    card.setAttribute(
      'data-item-card-config',
      JSON.stringify({
        serialNo: serialNos[0] || '',
        productName: title,
      }),
    );

    const body = document.createElement('div');
    body.className = 'item-card__body';

    const productWrapper = document.createElement('div');
    productWrapper.className = 'item-card__product-wrapper';

    const product = document.createElement('div');
    product.className = 'item-card__product item-card__product--merged';

    const productBody = document.createElement('div');
    productBody.className = 'item-card__product-body item-card__product-body--merged';

    const productNameEl = document.createElement('span');
    productNameEl.className = 'item-card__product-name';
    productNameEl.textContent = title;
    productBody.appendChild(productNameEl);

    productBody.appendChild(
      this._buildValueRow(
        serialNos,
        serialNoLabel,
        'data-item-card-serial-trigger',
        this._serialSidebarConfig,
      ),
    );
    productBody.appendChild(
      this._buildValueRow(
        patientNames,
        patientNameLabel,
        'data-item-card-patient-trigger',
        this._patientSidebarConfig,
      ),
    );

    product.appendChild(productBody);

    const line = document.createElement('div');
    line.className = 'item-card__line';

    productWrapper.append(product, line);

    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'item-card__remove';
    removeBtn.setAttribute('aria-label', 'Remove item');
    removeBtn.appendChild(createIcon('remove'));

    body.append(productWrapper, configuration, removeBtn);
    card.appendChild(body);

    return card;
  }

  _buildValueRow(values, labelText, triggerAttr, sidebarConfig) {
    const row = document.createElement('div');
    row.className = 'item-card__product-row';

    const label = document.createElement('span');
    label.className = 'item-card__product-label';
    label.textContent = labelText;

    const value = document.createElement('span');
    value.className = 'item-card__product-value';

    const useLinks = values.length > 2;
    const displayed = values.slice(0, 2);

    if (useLinks) {
      const link = document.createElement('a');
      link.href = '#';
      link.className = 'item-card__serial-link';
      link.setAttribute(triggerAttr, '');
      link.textContent = displayed.join(', ') + '+';
      // open sidebar with full list when clicked
      link.addEventListener('click', (e) => {
        e.preventDefault();
        if (sidebarConfig) {
          document.dispatchEvent(
            new CustomEvent('item-card-serials:open', {
              detail: { ...sidebarConfig, items: values },
            }),
          );
        }
      });
      value.appendChild(link);
    } else {
      value.textContent = displayed.join(', ') || '-';
    }

    row.append(label, value);
    return row;
  }

  _unmerge() {
    if (!this._mergedCard) return;

    // When the merged card was edited, that shared selection is applied to every
    // card; otherwise each card is restored to its own pre-merge selection.
    const currentState = this._captureState(this._movedConfiguration.element);
    const mergedState = this._hasMeaningfulState(currentState) ? currentState : null;

    this._restoreConfiguration();

    const restoredCards = [];
    document.querySelectorAll('[data-item-card-inactive]').forEach((card) => {
      card.removeAttribute('hidden');
      card.removeAttribute('data-item-card-inactive');
      card.setAttribute('data-item-card', '');
      restoredCards.push(card);
    });

    restoredCards.forEach((card, index) => {
      const configEl = card.querySelector('.item-card__configurator-mount');
      if (!configEl) return;
      const state = mergedState || this._savedCardSelections?.[index];
      this._resetState(configEl);
      this._applyState(configEl, state);
    });

    this._mergedCard.remove();
    this._mergedCard = null;
    this._mergedCardTouched = false;
    this._savedCardSelections = null;
  }

  _restoreConfiguration() {
    if (!this._movedConfiguration) return;

    const { element, parent } = this._movedConfiguration;
    const removeButton = parent.querySelector('.item-card__remove');
    if (removeButton) {
      parent.insertBefore(element, removeButton);
    } else {
      parent.appendChild(element);
    }
    this._movedConfiguration = null;
  }

  // Configuration state is captured/restored through each component's own DOM
  // and events, so both the prototype (ui-*) and configurator (dtp-*) markup
  // behave identically without modifying the configurator components.

  _isDtp(configEl) {
    return !!configEl.querySelector('.dtp-select__native, .dtp-input, .dtp-checkbox-input');
  }

  _captureState(configEl) {
    if (!configEl) return null;
    return this._isDtp(configEl) ? this._captureDtpState(configEl) : this._captureUiState(configEl);
  }

  _resetState(configEl) {
    if (!configEl) return;
    if (this._isDtp(configEl)) this._resetDtpState(configEl);
    else this._resetUiState(configEl);
  }

  _applyState(configEl, state) {
    if (!configEl || !state) return;
    if (state.variant === 'dtp') this._applyDtpState(configEl, state);
    else this._applyUiState(configEl, state);
  }

  _captureUiState(configEl) {
    const categoryItem = configEl.querySelector(
      '[data-dropdown-select] .ui-dropdown-menu__item[aria-selected="true"]',
    );
    const reasonIds = [
      ...configEl.querySelectorAll(
        '[data-dropdown-multi-select] .ui-dropdown-menu__item[aria-selected="true"]',
      ),
    ].map((li) => li.dataset.itemId);

    return {
      variant: 'ui',
      categoryId: categoryItem?.dataset.itemId ?? null,
      reasonIds,
    };
  }

  _resetUiState(configEl) {
    const categoryDrop = configEl.querySelector('[data-dropdown-select]');
    if (categoryDrop) {
      categoryDrop
        .querySelectorAll('.ui-dropdown-menu__item')
        .forEach((li) => li.setAttribute('aria-selected', 'false'));
      const label = categoryDrop.querySelector('[data-dropdown-select-label]');
      if (label) label.textContent = label.dataset.defaultLabel ?? '';
    }

    const reasonDrop = configEl.querySelector('[data-dropdown-multi-select]');
    if (reasonDrop) {
      reasonDrop.querySelectorAll('.ui-dropdown-menu__item').forEach((li) => {
        li.setAttribute('aria-selected', 'false');
        li.hidden = true;
      });
      const trigger = reasonDrop.querySelector('.ui-dropdown-select__trigger');
      if (trigger) trigger.disabled = true;
    }
  }

  _applyUiState(configEl, state) {
    if (state.categoryId) {
      const categoryButton = configEl.querySelector(
        `[data-dropdown-select] .ui-dropdown-menu__item[data-item-id="${state.categoryId}"] button`,
      );
      categoryButton?.click();
    }

    (state.reasonIds || []).forEach((id) => {
      const reasonButton = configEl.querySelector(
        `[data-dropdown-multi-select] .ui-dropdown-menu__item[data-item-id="${id}"] button`,
      );
      reasonButton?.click();
    });
  }

  _captureDtpState(configEl) {
    const categorized = [...configEl.querySelectorAll('.dtp-categorized-select')].map(
      (container) => ({
        category:
          container.querySelector('.dtp-categorized-select__category .dtp-select__native')?.value ??
          '',
        reasons: container.dtpCategorizedState
          ? container.dtpCategorizedState.getReasonIds()
          : [
              ...container.querySelectorAll(
                '.dtp-categorized-select__reason .dtp-select__option--selected',
              ),
            ].map((option) => option.getAttribute('data-value')),
      }),
    );
    const singles = [...configEl.querySelectorAll('.dtp-select:not(.dtp-multiselect)')]
      .filter((container) => !container.closest('.dtp-categorized-select'))
      .map((container) => container.querySelector('.dtp-select__native')?.value ?? '');
    const multis = [...configEl.querySelectorAll('.dtp-multiselect')]
      .filter((container) => !container.closest('.dtp-categorized-select'))
      .map((container) =>
        [...container.querySelectorAll('.dtp-select__option--selected')].map((option) =>
          option.getAttribute('data-value'),
        ),
      );
    const texts = [...configEl.querySelectorAll('input.dtp-input, textarea.dtp-input')].map(
      (input) => input.value,
    );
    const checks = [...configEl.querySelectorAll('.dtp-checkbox-input')].map(
      (input) => input.checked,
    );
    const files = [...configEl.querySelectorAll('input[type="file"]')].map(
      (input) => input.files?.[0] ?? null,
    );

    return { variant: 'dtp', categorized, singles, multis, texts, checks, files };
  }

  _hasMeaningfulState(state) {
    if (!state) return false;
    if (state.variant === 'ui') return state.reasonIds.length > 0;

    return (
      state.categorized.some((field) => field.reasons.length > 0) ||
      state.singles.some(Boolean) ||
      state.multis.some((field) => field.length > 0) ||
      state.texts.some((value) => value !== '') ||
      state.checks.some(Boolean) ||
      state.files.some(Boolean)
    );
  }

  _resetDtpState(configEl) {
    configEl
      .querySelectorAll('.dtp-categorized-select')
      .forEach((container) => container.dtpCategorizedState?.setReasonIds([]));

    configEl.querySelectorAll('.dtp-select').forEach((container) => {
      if (container.classList.contains('dtp-multiselect')) {
        container
          .querySelectorAll('.dtp-select__option--selected')
          .forEach((option) => option.click());
      } else {
        this._setDtpSingle(container, '');
      }
    });

    configEl.querySelectorAll('input.dtp-input, textarea.dtp-input').forEach((input) => {
      input.value = '';
      input.dispatchEvent(new Event('input', { bubbles: true }));
    });

    configEl.querySelectorAll('.dtp-checkbox-input').forEach((input) => {
      if (input.checked) {
        input.checked = false;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    configEl.querySelectorAll('input[type="file"]').forEach((input) => {
      if (input.files?.length) {
        input.value = '';
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  _applyDtpState(configEl, state) {
    (state.categorized || []).forEach((field, index) => {
      const containers = configEl.querySelectorAll('.dtp-categorized-select');
      const container = containers[index];
      if (!container) return;
      this._setDtpSingle(
        container.querySelector('.dtp-categorized-select__category'),
        field.category,
      );
    });

    const singles = [...configEl.querySelectorAll('.dtp-select:not(.dtp-multiselect)')];
    const standaloneSingles = singles.filter(
      (container) => !container.closest('.dtp-categorized-select'),
    );
    (state.singles || []).forEach((value, index) => {
      if (standaloneSingles[index]) this._setDtpSingle(standaloneSingles[index], value);
    });
    (state.categorized || []).forEach((field, index) => {
      const containers = configEl.querySelectorAll('.dtp-categorized-select');
      const container = containers[index];
      if (!container) return;
      if (container.dtpCategorizedState) {
        container.dtpCategorizedState.setReasonIds(field.reasons);
        return;
      }
      const reason = container.querySelector('.dtp-categorized-select__reason');
      (field.reasons || []).forEach((id) => {
        reason?.querySelector(`.dtp-select__option[data-value="${CSS.escape(id)}"]`)?.click();
      });
    });

    // Reason options are (re)rendered by the category change above, so their
    // option elements only exist after the single selects have been applied.
    const multis = [...configEl.querySelectorAll('.dtp-multiselect')];
    (state.multis || []).forEach((ids, index) => {
      const container = multis[index];
      if (!container) return;
      (ids || []).forEach((id) => {
        const option = [...container.querySelectorAll('.dtp-select__option')].find(
          (optionEl) => optionEl.getAttribute('data-value') === id,
        );
        option?.click();
      });
    });

    const texts = [...configEl.querySelectorAll('input.dtp-input, textarea.dtp-input')];
    (state.texts || []).forEach((value, index) => {
      const input = texts[index];
      if (!input) return;
      input.value = value;
      input.dispatchEvent(new Event('input', { bubbles: true }));
    });

    const checks = [...configEl.querySelectorAll('.dtp-checkbox-input')];
    (state.checks || []).forEach((checked, index) => {
      const input = checks[index];
      if (input && input.checked !== checked) {
        input.checked = checked;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    const fileInputs = [...configEl.querySelectorAll('input[type="file"]')];
    (state.files || []).forEach((file, index) => {
      const input = fileInputs[index];
      if (!input || !file) return;
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      input.files = dataTransfer.files;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  // Mirrors dtpSelect
  // states stay in sync when the value is set programmatically (empty string
  // reuses the native placeholder <option>, so no text is hardcoded).
  _setDtpSingle(container, value) {
    const native = container.querySelector('.dtp-select__native');
    if (!native) return;

    native.value = value || '';

    const valueLabel = container.querySelector('.dtp-select__value');
    if (valueLabel) {
      const selectedOption = [...native.options].find((option) => option.value === native.value);
      valueLabel.textContent = selectedOption ? selectedOption.textContent : '';
      valueLabel.classList.toggle('dtp-select__value--placeholder', !native.value);
    }

    container.querySelectorAll('.dtp-select__option').forEach((option) => {
      const isSelected = !!native.value && option.getAttribute('data-value') === native.value;
      option.setAttribute('aria-selected', String(isSelected));
      option.classList.toggle('dtp-select__option--selected', isSelected);
    });

    native.dispatchEvent(new Event('change', { bubbles: true }));
  }
}
